import { consola } from 'consola'
import { ref, computed, watch, readonly } from 'vue';
import { Capacitor } from '@capacitor/core';
import {
  PushNotifications,
  type Token,
  type PushNotificationSchema,
  type ActionPerformed,
} from '@capacitor/push-notifications';
import dayjs from 'dayjs';
import duration from 'dayjs/plugin/duration';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import { apiProfile } from '~/entities/user';
import APIAdminNotification from '~/fsd-pages/admin/api';

// Инициализируем плагины dayjs для работы с временными данными
// Следуем лучшим практикам Vue.js для работы с датами и временем
dayjs.extend(duration);
dayjs.extend(timezone);
dayjs.extend(utc);

export function usePushNotifications() {
  const currentToken = ref<string | null>(null);
  const initializationStatus = ref<'idle' | 'initializing' | 'success' | 'error'>('idle');
  const lastError = ref<string | null>(null);
  const lastInitAttempt = ref<string | null>(null);
  const initializationDuration = ref<number>(0);
  const retryCount = ref<number>(0);
  const lastTokenUpdate = ref<string | null>(null);
  const errorHistory = ref<Array<{ timestamp: string; error: string; context: string }>>([]);

  // Computed properties для улучшенной реактивности
  const isReady = computed(() => initializationStatus.value === 'success' && currentToken.value !== null);
  const hasToken = computed(() => currentToken.value !== null);
  const canRetry = computed(() => initializationStatus.value === 'error' && retryCount.value < 3);
  
  // Форматирование времени последней инициализации
  const formattedLastAttempt = computed(() => {
    if (!lastInitAttempt.value) return 'Никогда';
    return dayjs(lastInitAttempt.value).format('DD.MM.YYYY HH:mm:ss');
  });

  // Форматирование длительности инициализации
  const formattedInitDuration = computed(() => {
    if (initializationDuration.value === 0) return 'Н/Д';
    const duration = dayjs.duration(initializationDuration.value, 'milliseconds');
    if (duration.asSeconds() < 1) return `${Math.round(duration.asMilliseconds())}мс`;
    return `${duration.asSeconds().toFixed(1)}с`;
  });

  // Статистика ошибок
  const errorStats = computed(() => {
    const last24h = dayjs().subtract(24, 'hours');
    const recentErrors = errorHistory.value.filter(e => dayjs(e.timestamp).isAfter(last24h));
    return {
      total: errorHistory.value.length,
      last24h: recentErrors.length,
      lastErrorTime: errorHistory.value.length > 0 
        ? dayjs(errorHistory.value.at(-1).timestamp).format('HH:mm:ss')
        : null,
    };
  });

  function logError(context: string, error: any) {
    const timestamp = dayjs().toISOString();
    const errorMessage = `[Push Notifications ${context}]: ${error.message || error}`;
    
    // Расширенное логирование с временными метками
    consola.error(`🔔 ${dayjs().format('HH:mm:ss.SSS')} ${errorMessage}`, {
      context,
      error,
      timestamp,
      platform: Capacitor.getPlatform(),
      retryAttempt: retryCount.value,
    });
    
    lastError.value = errorMessage;
    
    // Добавляем в историю ошибок (ограничиваем до 50 записей)
    errorHistory.value.push({
      timestamp,
      error: errorMessage,
      context,
    });
    
    if (errorHistory.value.length > 50) {
      errorHistory.value = errorHistory.value.slice(-50);
    }
  }

  // Watcher для мониторинга изменений состояния
  watch(initializationStatus, (newStatus, oldStatus) => {
    if (newStatus !== oldStatus) {
      logSuccess('Status Change', `${oldStatus} → ${newStatus}`);
    }
  });

  // Улучшенная функция логирования для успешных операций
  function logSuccess(context: string, details?: any) {
    const timestamp = dayjs().format('HH:mm:ss.SSS');
    consola.debug(`🔔 ${timestamp} [Push Notifications ${context}] Success`, details);
  }

  async function initPush() {
    const startTime = dayjs();
    lastInitAttempt.value = startTime.toISOString();
    initializationDuration.value = 0;
    
    logSuccess('Initialization', 'Started');
    initializationStatus.value = 'initializing';
    lastError.value = null;
    
    try {
      // Проверка платформы
      const platform = Capacitor.getPlatform();
      logSuccess('Platform Check', `Platform: ${platform}`);
      
      if (platform === 'web') {
        logSuccess('Web Platform', 'Push notifications not supported on web');
        initializationStatus.value = 'success';
        initializationDuration.value = dayjs().diff(startTime);
        return;
      }

      // Проверка доступности PushNotifications API
      if (!PushNotifications) {
        throw new Error('PushNotifications API not available');
      }

      logSuccess('API Check', 'PushNotifications API available');
      
      logSuccess('Permission Check', 'Checking current permissions...');
      const { receive } = await PushNotifications.checkPermissions();
      logSuccess('Permission Status', `Current permissions: ${receive}`);
    
      // Обработка разрешений
      if (receive === 'prompt') {
        logSuccess('Permission Request', 'Requesting permissions...');
        const perm = await PushNotifications.requestPermissions();
        logSuccess('Permission Result', `Permission result: ${perm.receive}`);
        if (perm.receive !== 'granted') {
          throw new Error(`Push notifications permission denied: ${perm.receive}`);
        }
      } else if (receive === 'denied') {
        throw new Error('Push notifications permission is denied. Please enable in device settings.');
      }

      // Регистрация
      logSuccess('Registration', 'Registering push notifications...');
      await PushNotifications.register();
      logSuccess('Registration Complete', 'Push notifications registered successfully');

      // Добавляем обработчики событий
      logSuccess('Listeners Setup', 'Adding push notification listeners...');

      PushNotifications.addListener('registration', async (t: Token) => {
        logSuccess('Token Received', `Registration token received: ${t.value.slice(0, 20)}...`);
        const newToken = t.value;
        
        if (!newToken) {
          await logError('Registration', 'Empty token received');
          return;
        }
        
        if (newToken === currentToken.value) {
          logSuccess('Token Check', 'Token unchanged, skipping');
          return;
        }

        currentToken.value = newToken;
        lastTokenUpdate.value = dayjs().toISOString();
        logSuccess('Token Update', 'Saving token to server...');
        
        try {
          await apiProfile.addPushToken({
              token: newToken,
              platform: Capacitor.getPlatform(),
            });
          logSuccess('Token Save', 'Token saved to server successfully');
          initializationStatus.value = 'success';
          initializationDuration.value = dayjs().diff(startTime);
          retryCount.value = 0; // Сбрасываем счетчик попыток при успехе
        } catch (error) {
          await logError('Token Save', error);
          // Не блокируем инициализацию, если не удалось сохранить токен
        }
      });

      PushNotifications.addListener('registrationError', async (err) => {
        const errorDetails = {
          error: err.error,
          platform: Capacitor.getPlatform(),
          timestamp: dayjs().toISOString(),
        };
        
        await logError('Registration Error', err);
        consola.error(`🔔 ${dayjs().format('HH:mm:ss.SSS')} Registration Error Details:`, errorDetails);
        
        // Специфичные ошибки для разных платформ
        if (Capacitor.getPlatform() === 'ios') {
          consola.error('🔔 iOS Push Error - Check: GoogleService-Info.plist, Apple Developer Console, Certificates');
        } else if (Capacitor.getPlatform() === 'android') {
          consola.error('🔔 Android Push Error - Check: google-services.json, Firebase Console, Package Name');
        }
        
        retryCount.value += 1;
        initializationStatus.value = 'error';
        initializationDuration.value = dayjs().diff(startTime);
      });

      PushNotifications.addListener('pushNotificationReceived', async (payload: PushNotificationSchema) => {
        logSuccess('Notification Received', {
          title: payload.title,
          body: payload.body,
          id: payload.id,
          receivedAt: dayjs().format('HH:mm:ss.SSS'),
        });
        
        const notificationId = payload.data?.notificationId;
        if (notificationId) {
          try {
            await APIAdminNotification.markDelivered({ notificationId });
            logSuccess('Mark Delivered', `Notification ${notificationId} marked as delivered`);
          } catch (error) {
            await logError('Mark Delivered', error);
          }
        }
      });

      PushNotifications.addListener('pushNotificationActionPerformed', async (action: ActionPerformed) => {
        logSuccess('Notification Action', {
          actionId: action.actionId,
          title: action.notification?.title,
          performedAt: dayjs().format('HH:mm:ss.SSS'),
        });
        
        // Обрабатываем действие из уведомления
        const notificationData = action.notification?.data || {};
        
        try {
          // Используем универсальный обработчик действий
          const { useNotificationActionHandler } = await import('@features/notifications');
          const { handleNotificationAction } = useNotificationActionHandler();
          await handleNotificationAction(notificationData as any);
        } catch (error) {
          await logError('Handle Notification Action', error);
        }
      });

      logSuccess('Initialization Complete', `Completed in ${dayjs().diff(startTime)}ms`);
      
    } catch (error) {
      await logError('Initialization', error);
      initializationStatus.value = 'error';
      initializationDuration.value = dayjs().diff(startTime);
      retryCount.value += 1;
      throw error;
    }
  }

  // Функция повторной попытки инициализации с экспоненциальной задержкой
  async function retryInitialization(): Promise<boolean> {
    if (!canRetry.value) return false;
    
    const delay = Math.min(1000 * Math.pow(2, retryCount.value), 10_000); // max 10 секунд
    logSuccess('Retry Schedule', `Retrying in ${delay}ms (attempt ${retryCount.value + 1}/3)`);
    
    await new Promise(resolve => setTimeout(resolve, delay));
    
    try {
      await initPush();
      return true;
    } catch (error) {
      await logError('Retry Failed', error);
      return false;
    }
  }

  async function removePushToken() {
    if (!currentToken.value) {
      logSuccess('Token Remove', 'No token to remove');
      return;
    }
    
    try {
      logSuccess('Token Remove', 'Removing push token...');
      await apiProfile.removePushToken({ token: currentToken.value });
      currentToken.value = null;
      lastTokenUpdate.value = null;
      logSuccess('Token Remove', 'Push token removed successfully');
    } catch (error) {
      await logError('Token Remove', error);
    }
  }

  async function forceRefreshPushToken() {
    try {
      logSuccess('Token Refresh', 'Force refreshing push token...');
      await PushNotifications.removeAllListeners();
      
      const perm = await PushNotifications.requestPermissions();
      if (perm.receive !== 'granted') {
        await logError('Token Refresh', 'Permissions not granted');
        return;
      }
      
      await PushNotifications.register();
      logSuccess('Token Refresh', 'Token refresh initiated');
    } catch (error) {
      await logError('Token Refresh', error);
    }
  }

  // Функция получения диагностической информации
  function getDiagnostics() {
    return {
      currentToken:
        currentToken.value != null && currentToken.value !== ''
          ? `${currentToken.value.slice(0, 20)}...`
          : null,
      status: initializationStatus.value,
      lastAttempt: formattedLastAttempt.value,
      duration: formattedInitDuration.value,
      retryCount: retryCount.value,
      canRetry: canRetry.value,
      isReady: isReady.value,
      errorStats: errorStats.value,
      platform: Capacitor.getPlatform(),
      lastTokenUpdate: lastTokenUpdate.value 
        ? dayjs(lastTokenUpdate.value).format('DD.MM.YYYY HH:mm:ss') 
        : null,
    };
  }

  return {
    // Основные функции
    initPush,
    removePushToken,
    forceRefreshPushToken,
    retryInitialization,
    getDiagnostics,
    
    // Реактивные состояния (только для чтения)
    currentToken: readonly(currentToken),
    initializationStatus: readonly(initializationStatus),
    lastError: readonly(lastError),
    
    // Computed properties
    isReady,
    hasToken,
    canRetry,
    formattedLastAttempt,
    formattedInitDuration,
    errorStats,
    
    // Дополнительная информация
    retryCount: readonly(retryCount),
    lastTokenUpdate: readonly(lastTokenUpdate),
    errorHistory: readonly(errorHistory),
  };
}
